import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, SimpleChanges, OnChanges, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { DataService } from '@app-core/services/data/data.service';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, finalize, map, startWith, takeUntil } from 'rxjs/operators';
import { environment } from '@env/environment';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { ConfirmDeleteDialogComponent } from '../confirm-delete-dialog/confirm-delete-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

export interface TagNode {
  tagId: number;
  tagName: string;
  rootTagId: number;
  parentTagId: number | null;
  tagStatus: string;
  entityName: string;
  path: string | null;
  children: TagNode[];
}

export interface FlatTagNode {
  tagName: string;
  rootTagId: number;
  parentTagId: number | null;
  level: number;
  hasChildren: boolean;
  tagStatus: string;
}

export interface AttributeItem {
  attributeName: string;
  attributeId: number;
  attributeType: string;
  status: string;
  tags: any[];
  tagTree: TagNode[];
}

@Component({
  selector: 'app-user-assign-tags',
  templateUrl: './user-assign-tags.component.html',
  styleUrls: ['./user-assign-tags.component.scss'],
})
export class UserAssignTagsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() attributeType = '';
  @Input() roleId = '';
  @Input() entityType = '';
  @Input() private readonly filterOutAccessTags = false;
  @Input() public assignedTagList: any[] = [];
  @Output() ruleChange = new EventEmitter<any>();
  @Input() tagAccessRule: any = null;
  @Output() selectedTagNames = new EventEmitter<string[]>();

  formArray: FormArray;
  groupRules: any[] = [];

  conditionGroups: any[] = [];
  conditionType: 'AND' | 'OR' = 'OR';
  selectedAttributes: string[] = [];
  expandedTags: { [groupIndex: number]: Set<string> } = {};

  public fleetId: string = '';
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  attributeList: any;
  attributeNames: AttributeItem[] = [];
  getAllTagsLoader: boolean;
  totalTags: number;
  allowedUserTags: any[];
  currentFleetInfo: any;

  // ── Loop-prevention guards ──────────────────────────────────────────────────
  private _pendingTagAccessRule: any = null;
  private _isHydrating = false;
  private _lastHydratedRule: any = null;

  // ── Custom tag panel state ──────────────────────────────────────────────────
  activePanelGroupIndex: number | null = null;
  tagPanelBreadcrumb: { [rowIndex: number]: { tagName: string; attributeName: string; node: TagNode }[] } = {};
  tagSearchQuery: { [rowIndex: number]: string } = {};
  private _documentClickHandler: (event: Event) => void;

  tagPanelDepth: { [key: number]: number } = {};
  isConnectorMenuOpen = false;
  constructor(
    private fb: FormBuilder,
    public dataService: DataService,
    private taggingService: TaggingService,
    private cdr: ChangeDetectorRef,
    private snackbarService: SnackBarService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public translate: TranslateService
  ) {
    this.formArray = this.fb.array([]);
  }

  ngOnInit() {
    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
        this.getAttributes();
      }
    });
    if (!this.tagAccessRule) {
      this.addCondition();
    }

    this._documentClickHandler = (event: Event) => {
      const target = event.target as HTMLElement;

      if (!target.closest('.tag-select-wrapper') && !target.closest('.tag-panel-overlay')) {
        setTimeout(() => {
          this.activePanelGroupIndex = null;
          this.cdr.detectChanges();
        }, 0);
      }
    };
    document.addEventListener('click', this._documentClickHandler);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    document.removeEventListener('click', this._documentClickHandler);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tagAccessRule'] && this.tagAccessRule && !this._isHydrating) {
      const incoming = JSON.stringify(this.tagAccessRule);
      const lastHydrated = JSON.stringify(this._lastHydratedRule);

      if (incoming !== lastHydrated) {
        this._pendingTagAccessRule = this.tagAccessRule;

        if (this.attributeNames?.length) {
          this.hydrateFromRule(this._pendingTagAccessRule);
          this._pendingTagAccessRule = null;
        }
      }
      this.cdr.detectChanges();
    }
  }
  toggleConnectorMenu(): void {
    this.isConnectorMenuOpen = !this.isConnectorMenuOpen;
  }

  setConditionType(type: 'AND' | 'OR') {
    this.conditionType = type;
    this.isConnectorMenuOpen = false;
    this.emitRule();
  }

  // ─── Build Tag Tree ──────────────────────────────────────────────────────────

  buildTagTree(tags: any[]): TagNode[] {
    const seen = new Set<number>();
    const uniqueTags = tags.filter((tag) => {
      if (seen.has(tag.rootTagId)) return false;
      seen.add(tag.rootTagId);
      return true;
    });

    const nodeMap = new Map<number, TagNode>();

    uniqueTags.forEach((tag) => {
      nodeMap.set(tag.rootTagId, {
        tagId: tag.tagId,
        tagName: tag.tagName,
        rootTagId: tag.rootTagId,
        parentTagId: tag.parentTagId,
        tagStatus: tag.tagStatus,
        entityName: tag.entityName,
        path: tag.path,
        children: [],
      });
    });

    const roots: TagNode[] = [];

    uniqueTags.forEach((tag) => {
      const node = nodeMap.get(tag.rootTagId)!;
      if (tag.parentTagId === null) {
        roots.push(node);
      } else {
        const parent = nodeMap.get(tag.parentTagId);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    return roots;
  }

  // ─── Flatten Tree ─────────────────────────────────────────────────────────────

  getFlatTagListForGroup(group: FormGroup): FlatTagNode[] {
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    if (!attrObj) return [];
    const groupIndex = this.formArray.controls.indexOf(group);
    return this.flattenTreeWithExpansion(attrObj.tagTree, 0, groupIndex);
  }

  private flattenTreeWithExpansion(nodes: TagNode[], level: number, groupIndex: number): FlatTagNode[] {
    const result: FlatTagNode[] = [];
    nodes.forEach((node) => {
      result.push({
        tagName: node.tagName,
        rootTagId: node.rootTagId,
        parentTagId: node.parentTagId,
        level,
        hasChildren: node.children.length > 0,
        tagStatus: node.tagStatus,
      });
      if (node.children.length > 0 && this.expandedTags[groupIndex]?.has(node.tagName)) {
        result.push(...this.flattenTreeWithExpansion(node.children, level + 1, groupIndex));
      }
    });
    return result;
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  get options(): string[] {
    return this.attributeNames.filter((attr) => attr.status?.toUpperCase() === 'ACTIVE').map((attr) => attr.attributeName);
  }

  get totalItems(): number {
    return this.formArray.length + this.conditionGroups.length;
  }

  getAttrObj(attributeName: string): AttributeItem | undefined {
    return this.attributeNames.find((a) => a.attributeName === attributeName);
  }

  getAllDescendantNames(node: TagNode): string[] {
    let names: string[] = [];
    node.children.forEach((child) => {
      names.push(child.tagName);
      names = names.concat(this.getAllDescendantNames(child));
    });
    return names;
  }

  findNodeByRootTagId(nodes: TagNode[], rootTagId: number): TagNode | null {
    for (const node of nodes) {
      if (node.rootTagId === rootTagId) return node;
      const found = this.findNodeByRootTagId(node.children, rootTagId);
      if (found) return found;
    }
    return null;
  }

  // ─── Form Group ──────────────────────────────────────────────────────────────

  createConditionGroup(): FormGroup {
    const group = this.fb.group({
      myControl: this.fb.control<string>(''),
      toppings: this.fb.control<string[]>([]),
      subtags: this.fb.control<string[]>([]),
      filteredOptions: this.fb.control<Observable<string[]>>(null as any),
      availableTags: this.fb.control<string[]>([]),
      availableSubtags: this.fb.control<string[]>([]),
      flatTags: this.fb.control<FlatTagNode[]>([]),
    });

    const control = group.get('myControl') as FormControl;

    group.patchValue({
      filteredOptions: control.valueChanges.pipe(
        startWith(''),
        distinctUntilChanged(),
        map((value: string) => this._filter(value || '', control))
      ),
    });

    return group;
  }

  onAttributeSelected(selectedAttribute: string, group: FormGroup) {
    const attrObj = this.getAttrObj(selectedAttribute);
    if (!attrObj) return;

    const groupIndex = this.formArray.controls.indexOf(group);
    this.expandedTags[groupIndex] = new Set<string>();

    group.patchValue({ toppings: [], subtags: [] }, { emitEvent: false });

    const flatTags = this.flattenTreeWithExpansion(attrObj.tagTree, 0, groupIndex);
    group.patchValue({ flatTags }, { emitEvent: false });

    // Reset panel state for this row
    this.tagPanelBreadcrumb[groupIndex] = null;
    this.tagSearchQuery[groupIndex] = '';

    this.updateSelectedAttributes();
    this.refreshFiltersExcept(group);
  }

  addCondition() {
    if (this.conditionGroups.length && this.hasConditionGroupData()) {
      this.snackbarService.failure(this.translate.instant('Please clear existing group conditions before switching to rows'));
      return;
    }
    if (this.conditionGroups.length && !this.hasConditionGroupData()) {
      this.conditionGroups = [];
    }
    const group = this.createConditionGroup();
    this.formArray.push(group);
  }

  addConditionGroup() {
    if (this.conditionGroups.length >= 5) {
      this.snackbarService.failure(this.translate.instant('Groups must not have more than 5 entries'));
      return;
    }
    if (this.formArray.length && this.hasFormArrayData()) {
      this.snackbarService.failure(this.translate.instant('Please clear existing row conditions before switching to groups'));
      return;
    }
    if (this.formArray.length && !this.hasFormArrayData()) {
      this.formArray.clear();
    }
    this.conditionGroups.push({});
  }

  private hasFormArrayData(): boolean {
    return this.formArray.controls.some((group) => {
      const attributeName = group.get('myControl')?.value;
      const selectedTags = group.get('toppings')?.value || [];
      return !!attributeName || selectedTags.length > 0;
    });
  }

  private hasConditionGroupData(): boolean {
    return this.conditionGroups.some((cg) => {
      const rule = cg?.rule;
      if (!rule) return false;
      return (rule.rows || []).some((row: any) => row?.tagIds?.length > 0);
    });
  }

  removeConditionGroup(index: number) {
    this.conditionGroups.splice(index, 1);
  }

  onClickClear(index: number) {
    const group = this.formArray.at(index) as FormGroup;
    const control = group.get('myControl') as FormControl;

    group.reset({
      myControl: '',
      toppings: [],
      subtags: [],
      availableTags: [],
      availableSubtags: [],
    });

    group.patchValue({
      filteredOptions: control.valueChanges.pipe(
        startWith(''),
        distinctUntilChanged(),
        map((value: string) => this._filter(value || '', control))
      ),
    });

    this.tagPanelBreadcrumb[index] = null;
    this.tagSearchQuery[index] = '';

    this.updateSelectedAttributes();
    this.refreshFilters();
    this.emitRule();
    this.snackbarService.success(this.translate.instant('Selection has been cleared.'));
  }

  onClickDelete(index: number) {
    if (this.formArray.length <= 1 && !this.conditionGroups.length) return;

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '460px',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      // Save snapshot for undo
      const deletedGroup = this.formArray.at(index) as FormGroup;
      const snapshot = {
        myControl: deletedGroup.get('myControl')?.value,
        toppings: [...(deletedGroup.get('toppings')?.value || [])],
        subtags: [...(deletedGroup.get('subtags')?.value || [])],
      };

      // Perform delete
      this.formArray.removeAt(index);
      this.updateSelectedAttributes();
      this.refreshFilters();
      this.emitRule();

      // ✅ No custom component needed — use snackBar.open() directly
      const snackRef = this.snackBar.open(this.translate.instant('Condition deleted successfully'), this.translate.instant('Undo'), {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: 'undo-snackbar',
      });

      // Handle Undo
      snackRef.onAction().subscribe(() => {
        const restoredGroup = this.createConditionGroup();

        if (snapshot.myControl) {
          restoredGroup.patchValue({ myControl: snapshot.myControl }, { emitEvent: false });

          const attrObj = this.getAttrObj(snapshot.myControl);
          if (attrObj) {
            this.expandedTags[index] = new Set<string>();
            const flatTags = this.flattenTreeWithExpansion(attrObj.tagTree, 0, index);
            restoredGroup.patchValue({ toppings: snapshot.toppings, subtags: snapshot.subtags, flatTags }, { emitEvent: false });
          }
        }

        this.formArray.insert(index, restoredGroup);
        this.updateSelectedAttributes();
        this.refreshFilters();
        this.emitRule();
        this.cdr.detectChanges();
      });
    });
  }

  onClearAll() {
    // ✅ Snapshot everything before clearing
    const snapshot = {
      formArray: this.formArray.controls.map((group: any) => ({
        myControl: group.get('myControl')?.value,
        toppings: [...(group.get('toppings')?.value || [])],
        subtags: [...(group.get('subtags')?.value || [])],
      })),
      conditionGroups: [...this.conditionGroups],
      conditionType: this.conditionType,
    };

    this.formArray.clear();
    this.conditionGroups = [];
    this.groupRules = [];
    this.activePanelGroupIndex = null;
    this.tagPanelBreadcrumb = {};
    this.tagSearchQuery = {};
    this.addCondition();
    this.emitRule();
    this.ruleChange.emit();

    // ✅ Snackbar with Undo action
    const snackRef = this.snackBar.open(this.translate.instant('Tag(s) have been cleared successfully.'), this.translate.instant('Undo'), {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: 'undo-snackbar',
    });

    snackRef.onAction().subscribe(() => {
      this.formArray.clear();
      this.conditionGroups = [];

      this.addCondition();

      this.conditionType = snapshot.conditionType;

      snapshot.formArray.forEach((savedGroup, index) => {
        const restoredGroup = this.createConditionGroup();

        if (savedGroup.myControl) {
          restoredGroup.patchValue({ myControl: savedGroup.myControl }, { emitEvent: false });

          const attrObj = this.getAttrObj(savedGroup.myControl);
          if (attrObj) {
            this.expandedTags[index] = new Set<string>();
            const flatTags = this.flattenTreeWithExpansion(attrObj.tagTree, 0, index);
            restoredGroup.patchValue({ toppings: savedGroup.toppings, subtags: savedGroup.subtags, flatTags }, { emitEvent: false });
          }
        }

        this.formArray.push(restoredGroup);
      });

      if (snapshot.conditionGroups.length) {
        this.conditionGroups = [...snapshot.conditionGroups];
      }

      this.updateSelectedAttributes();
      this.refreshFilters();
      this.emitRule();
      this.ruleChange.emit();
      this.cdr.detectChanges();
    });
  }

  private updateSelectedAttributes() {
    this.selectedAttributes = this.formArray.controls.map((g) => g.get('myControl')?.value).filter((v) => !!v && this.options.includes(v));
  }

  private refreshFilters() {
    this.formArray.controls.forEach((group) => {
      const control = group.get('myControl') as FormControl;
      const obs = control.valueChanges.pipe(
        startWith(control.value || ''),
        distinctUntilChanged(),
        map((value: string) => this._filter(value || '', control))
      );
      group.patchValue({ filteredOptions: obs }, { emitEvent: false });
    });
  }

  private refreshFiltersExcept(excludeGroup: FormGroup) {
    this.formArray.controls.forEach((g) => {
      if (g === excludeGroup) return;
      const control = g.get('myControl') as FormControl;
      const obs = control.valueChanges.pipe(
        startWith(control.value || ''),
        distinctUntilChanged(),
        map((value: string) => this._filter(value || '', control))
      );
      g.patchValue({ filteredOptions: obs }, { emitEvent: false });
    });
  }

  private _filter(value: string, control: FormControl): string[] {
    const filterValue = value.toLowerCase();
    const otherSelectedAttributes = this.formArray.controls
      .filter((g) => g.get('myControl') !== control)
      .map((g) => g.get('myControl')?.value)
      .filter((v) => !!v);
    return this.options.filter((option) => option.toLowerCase().includes(filterValue) && !otherSelectedAttributes.includes(option));
  }

  // ─── Custom Tag Panel ─────────────────────────────────────────────────────────

  trackByTagName(_index: number, item: FlatTagNode): string {
    return item.tagName;
  }

  // Explicit method avoids Angular template truthy issues with index 0
  isPanelOpen(rowIndex: number): boolean {
    return this.activePanelGroupIndex === rowIndex;
  }

  openTagPanel(event: Event, group: FormGroup, rowIndex: number) {
    event.stopPropagation();
    const attribute = group.get('myControl')?.value;
    if (!attribute) {
      this.snackbarService.failure(this.translate.instant('Please select an attribute first'));
      return;
    }
    if (this.activePanelGroupIndex === rowIndex) {
      this.activePanelGroupIndex = null;
    } else {
      this.activePanelGroupIndex = rowIndex;
      this.tagPanelBreadcrumb[rowIndex] = []; // ← array instead of null
      this.tagSearchQuery[rowIndex] = '';
    }
    this.cdr.detectChanges();
  }

  getVisibleTagsForPanel(group: FormGroup, rowIndex: number): FlatTagNode[] {
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    if (!attrObj) return [];

    const breadcrumbs = this.tagPanelBreadcrumb[rowIndex] || [];
    const query = (this.tagSearchQuery[rowIndex] || '').toLowerCase().trim();

    // use last breadcrumb's node children, or root
    const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
    const sourceNodes: TagNode[] = lastCrumb ? lastCrumb.node.children : attrObj.tagTree;

    const flat: FlatTagNode[] = sourceNodes.map((node) => ({
      tagName: node.tagName,
      rootTagId: node.rootTagId,
      parentTagId: node.parentTagId,
      level: 0,
      hasChildren: node.children.length > 0,
      tagStatus: node.tagStatus,
    }));

    if (!query) return flat;
    return flat.filter((f) => f.tagName.toLowerCase().includes(query));
  }

  getChildCount(group: FormGroup, flatNode: FlatTagNode): number {
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    if (!attrObj) return 0;
    const node = this.findNodeByRootTagId(attrObj.tagTree, flatNode.rootTagId);
    return node?.children.length || 0;
  }

  drillInto(rowIndex: number, group: FormGroup, flatNode: FlatTagNode) {
    this.tagPanelDepth[rowIndex] = (this.tagPanelDepth[rowIndex] ?? 0) + 1;
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    if (!attrObj) return;
    const node = this.findNodeByRootTagId(attrObj.tagTree, flatNode.rootTagId);
    if (!node) return;

    if (!this.tagPanelBreadcrumb[rowIndex]) {
      this.tagPanelBreadcrumb[rowIndex] = [];
    }
    // push to array instead of replacing
    this.tagPanelBreadcrumb[rowIndex] = [
      ...this.tagPanelBreadcrumb[rowIndex],
      { tagName: flatNode.tagName, attributeName: attribute, node },
    ];

    this.tagSearchQuery[rowIndex] = '';
    this.cdr.detectChanges();
  }

  drillUp(rowIndex: number) {
    this.tagPanelDepth[rowIndex] = Math.max((this.tagPanelDepth[rowIndex] ?? 1) - 1, 0);
    if (this.tagPanelBreadcrumb[rowIndex]?.length) {
      // pop last item instead of clearing
      this.tagPanelBreadcrumb[rowIndex] = this.tagPanelBreadcrumb[rowIndex].slice(0, -1);
    }
    this.tagSearchQuery[rowIndex] = '';
    this.cdr.detectChanges();
  }

  isTagSelected(group: FormGroup, tagName: string): boolean {
    const selected: string[] = group.get('toppings')?.value || [];
    return selected.includes(tagName);
  }
  closeTagPanel(i: number): void {
    this.tagPanelDepth[i] = 0;
  }

  onPanelTagToggle(group: FormGroup, flatNode: FlatTagNode) {
    const control = group.get('toppings') as FormControl;
    const currentValue: string[] = [...(control.value || [])];
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);

    const node = attrObj ? this.findNodeByRootTagId(attrObj.tagTree, flatNode.rootTagId) : null;
    const allDescendants = node ? this.getAllDescendantNames(node) : [];

    const isSelected = currentValue.includes(flatNode.tagName);

    if (!isSelected) {
      if (currentValue.length >= 10) {
        this.snackbarService.failure(this.translate.instant('Only 10 tags can be selected'));
        return;
      }
      const getAncestorNames = (nodes: TagNode[], targetId: number, ancestors: string[] = []): string[] | null => {
        for (const n of nodes) {
          if (n.rootTagId === targetId) return ancestors;
          const found = getAncestorNames(n.children, targetId, [...ancestors, n.tagName]);
          if (found) return found;
        }
        return null;
      };
      const ancestorNames = attrObj ? getAncestorNames(attrObj.tagTree, flatNode.rootTagId) || [] : [];

      if (ancestorNames.some((a) => currentValue.includes(a))) {
        this.snackbarService.failure(
          this.translate.instant('parentTagAlreadySelected', {
            tagName: flatNode.tagName,
          })
        );
        return;
      }
      const alreadySelectedDescendant = allDescendants.find((d) => currentValue.includes(d));
      if (alreadySelectedDescendant) {
        this.snackbarService.failure(
          this.translate.instant('childTagAlreadySelected', {
            tagName: flatNode.tagName,
            childName: alreadySelectedDescendant,
          })
        );
        return;
      }
      control.setValue(Array.from(new Set([...currentValue, flatNode.tagName])), { emitEvent: false });
    } else {
      control.setValue(
        currentValue.filter((v) => v !== flatNode.tagName),
        { emitEvent: false }
      );
    }

    this.emitRule();
    this.cdr.detectChanges();
  }

  clearAllTags(group: FormGroup) {
    const control = group.get('toppings') as FormControl;
    control.setValue([], { emitEvent: false });
    this.emitRule();
    this.cdr.detectChanges();
  }

  getFooterText(group: FormGroup): string {
    const selected: string[] = group.get('toppings')?.value || [];
    if (!selected.length) return '';
    const last = selected[selected.length - 1]; // most recently selected
    const rest = selected.length - 1;
    return rest > 0 ? `${last} (+${rest} more)` : last;
  }

  // ─── (kept for chip removal only) ────────────────────────────────────────────
  removeChip(selected: string, group: FormGroup) {
    const control = group.get('toppings') as FormControl;
    const currentValue: string[] = control.value || [];
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    const tagObj = attrObj?.tags.find((t: any) => t.tagName === selected);

    if (!tagObj) {
      control.setValue(currentValue.filter((v) => v !== selected));
      return;
    }

    const node = attrObj ? this.findNodeByRootTagId(attrObj.tagTree, tagObj.rootTagId) : null;
    const toRemove = new Set([selected, ...(node ? this.getAllDescendantNames(node) : [])]);
    control.setValue(currentValue.filter((v) => !toRemove.has(v)));
    this.emitRule();
  }

  public getAttributes(isRefresh?: boolean): void {
    if (!this.fleetId) return;

    this.getAllTagsLoader = true;
    this.totalTags = 0;
    this.allowedUserTags = [];

    const params: any = {
      fleetId: this.fleetId,
      limit: 100,
      offset: 0,
    };
    if (this.entityType) params['entityNames[]'] = this.entityType;
    if (this.roleId) params['roleIds[]'] = this.roleId;
    if (this.attributeType) params['attributeType'] = this.attributeType;

    this.taggingService
      .getAttributes(params, isRefresh)
      .pipe(
        finalize(() => (this.getAllTagsLoader = false)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: (res: any = {}) => {
          if (Boolean(environment.isGeotabEnv)) {
            const valid = ['DATA', 'ACCESS'];
            res.data = (res?.data || []).map((a: any) => ({
              ...a,
              attributeType: valid.includes(a.attributeType) ? a.attributeType : 'ACCESS',
            }));
            if (this.filterOutAccessTags) {
              res.data = res.data.filter((a: any) => a.attributeType === 'ACCESS');
            }
          }

          const expanded: any[] = (res?.data || []).flatMap((attr: any) => {
            const { entities = [], tags = [] } = attr;
            this.totalTags += tags.length;
            if (entities.length < 2) return [attr];
            return entities.map((e: string) => ({
              ...attr,
              optionGroupLabel: `${attr.attributeName} (${e})`,
              tags: tags.filter((t: any) => t.entityName === e),
            }));
          });

          expanded.forEach((a) => a.tags?.sort((x: any, y: any) => x.tagName.localeCompare(y.tagName)));

          const [dataAttrs, accessAttrs] = expanded.reduce(
            (acc: [any[], any[]], a: any) => {
              acc[a.attributeType === 'DATA' ? 0 : 1].push(a);
              return acc;
            },
            [[], []]
          );

          const userAttrIds = [...new Set((this.currentFleetInfo?.userTags || []).map((t: any) => t.attributeId))];
          const userTagIds = (this.currentFleetInfo?.userTags || []).map((t: any) => t.tagId);

          let allowedAccess: any[];
          if (userAttrIds.length) {
            allowedAccess = accessAttrs
              .filter((a: any) => userAttrIds.includes(a.attributeId))
              .map((a: any) => {
                const allowed = (a.tags || []).filter((t: any) => userTagIds.includes(t.tagId));
                this.allowedUserTags.push(...allowed);
                return { ...a, tags: allowed };
              });

            if (!this.assignedTagList.length) {
              this.assignedTagList = [...this.allowedUserTags];
            }
          } else {
            allowedAccess = accessAttrs;
          }

          const combined = [...dataAttrs, ...allowedAccess];

          this.attributeList = combined;
          this.attributeNames = combined.map((attr: any) => ({
            attributeName: attr.attributeName,
            attributeId: attr.attributeId,
            attributeType: attr.attributeType,
            optionGroupLabel: attr.optionGroupLabel,
            status: attr.status,
            tags: attr.tags || [],
            tagTree: this.buildTagTree(attr.tags || []),
          }));

          this.refreshFilters();

          if (this._pendingTagAccessRule) {
            this.hydrateFromRule(this._pendingTagAccessRule);
            this._pendingTagAccessRule = null;
          }
        },
        error: () => {
          this.getAllTagsLoader = false;
        },
      });
  }

  // ─── Hydrate from saved rule ─────────────────────────────────────────────────

  private hydrateFromRule(rule: any) {
    if (this._isHydrating) return;
    this._isHydrating = true;
    this._lastHydratedRule = rule;

    this.formArray.clear();
    this.conditionGroups = [];
    this.groupRules = [];
    this.activePanelGroupIndex = null;
    this.tagPanelBreadcrumb = {};
    this.tagSearchQuery = {};

    this.conditionType = rule.globalGroupOp || 'OR';

    const groups = rule.groups || [];
    if (groups.length === 0) {
      this.addCondition();
      this._isHydrating = false;
      return;
    }

    if (groups.length === 1) {
      const group = groups[0];
      this.conditionType = group.groupOp || 'OR';

      (group.rows || []).forEach((row: any) => {
        const attrObj = this.attributeNames.find((a) => String(a.attributeId) === String(row.attributeId));
        if (!attrObj) return;

        const conditionGroup = this.createConditionGroup();
        this.formArray.push(conditionGroup);

        conditionGroup.patchValue({ myControl: attrObj.attributeName }, { emitEvent: false });

        const rowIndex = this.formArray.controls.indexOf(conditionGroup);
        this.expandedTags[rowIndex] = new Set<string>();
        const flatTags = this.flattenTreeWithExpansion(attrObj.tagTree, 0, rowIndex);
        conditionGroup.patchValue({ flatTags }, { emitEvent: false });

        const selectedTagNames = (row.tagIds || [])
          .map((id: number) => attrObj.tags.find((t: any) => t.tagId === id)?.tagName)
          .filter(Boolean);

        conditionGroup.patchValue({ toppings: selectedTagNames }, { emitEvent: false });
      });
    } else {
      this.conditionGroups = groups.map((group: any) => ({ rule: group }));
      this.groupRules = [...groups];
    }
    this.refreshFilters();
    this._isHydrating = false;
    this.emitRule();
  }

  updateGroupRule(index: number, rule: any) {
    this.groupRules[index] = rule;
    this.emitRule();
  }

  buildTagAccessRule() {
    const groups: any[] = [];

    if (this.formArray.length) {
      const rows = this.formArray.controls
        .map((group: FormGroup) => {
          const attributeName = group.get('myControl')?.value;
          const selectedTags = group.get('toppings')?.value || [];
          const attrObj = this.getAttrObj(attributeName);

          if (!attrObj || !selectedTags.length) return null;

          const tagIds = selectedTags
            .map((tagName) => {
              const tag = attrObj.tags.find((t) => t.tagName === tagName);
              return tag?.tagId;
            })
            .filter(Boolean);

          return { attributeId: attrObj.attributeId, tagIds };
        })
        .filter(Boolean);

      if (rows.length) {
        groups.push({ groupOp: this.conditionType, rows });
      }
    }

    // ✅ FIX HERE
    if (this.groupRules.length) {
      const validGroups = this.groupRules.filter((g: any) => (g?.rows || []).some((r: any) => r?.tagIds?.length > 0));

      groups.push(...validGroups);
    }

    return { globalGroupOp: this.conditionType, groups };
  }

  emitRule() {
    if (this._isHydrating) return;
    const rule = this.buildTagAccessRule();
    this._lastHydratedRule = rule;
    this.ruleChange.emit(rule);
    // ← emit flat tag names for parent
    const names = this.formArray.controls.flatMap((group: any) => group.get('toppings')?.value || []);
    // Collect names from nested groups
    const groupNames = this.groupRules.flatMap((g: any) => g.tagNames || []);

    const allNames = [...names, ...groupNames];

    // Emit the final combined list to EditUserV2
    this.selectedTagNames.emit(allNames);
  }

  getPanelLevel(index: number): string {
    const depth = this.tagPanelDepth[index] ?? 0;
    const level = Math.min(depth + 1, 10); // cap at level 10
    return level < 10 ? '0' + level : '' + level;
  }
}
